import { Button, Col, DatePicker, Row, Tabs } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import './index.less'
import ReactECharts from 'echarts-for-react'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import { getQuery, getQueryRange } from '@/server'
import { useAppSelector } from '@/hooks'

dayjs.locale('zh-cn')

const { RangePicker } = DatePicker

export function DashboardPage() {
  const token = useAppSelector((state) => state.login.token)
  const [startDate, setStartDate] = useState(() => dayjs('2022-08-10').startOf('day').valueOf())
  const [endDate, setEndDate] = useState(() => dayjs().valueOf())

  const [buckets, setBuckets] = useState(4)
  const [objects, setObjects] = useState(7.9)
  const [servers, setServers] = useState({
    online: 2,
    offline: 0
  })
  const [drives, setDrives] = useState({
    online: 8,
    offline: 0
  })
  const [capacity, setCapacity] = useState({
    free: 2.3,
    total: 6.8
  })
  const [network, setNetwork] = useState({
    get: 39.0,
    put: 487.0
  })
  const [timeSinceLastHealActivity, setTimeSinceLastHealActivity] = useState(0)
  const [timeSinceLastScanActivity, setTimeSinceLastScanActivity] = useState(0)
  const [uptime, setUptime] = useState(0)

  const [capacityOption, setCapacityOption] = useState({})
  const [dataUsageOption, setDataUsageOption] = useState({})
  const [objectSizeOption, setObjectSizeOption] = useState({})
  const [dataReceiveOption, setDataReceiveOption] = useState({})
  const [dataSendOption, setDataSendOption] = useState({})

  const [apiRequestOption, setApiRequestOption] = useState({})
  const [apiRequestErrorOption, setApiRequestErrorOption] = useState({})
  const [internodeDataOption, setInternodeDataOption] = useState({})
  const [nodeIOOption, setNodeIOOption] = useState({})

  const [nodeMemoryOption, setNodeMemoryOption] = useState({})
  const [nodeCPUOption, setNodeCPUOption] = useState({})
  const [drivesFreeOption, setDriveFreeOption] = useState({})
  const [drivesUsedOption, setDrivesUsedOption] = useState({})
  const [nodeSyscallsOption, setNodeSyscallsOption] = useState({})
  const [nodeFileOption, setNodeFileOption] = useState({})

  const getMinIOData = () => {
    getQuery(
      {
        query: 'minio_bucket_usage_object_total',
        time: new Date()
      },
      token
    )
      .then((res) => {
        // console.log(res)
        setBuckets(res.data.data.result.length)
        let objectCnt = 0
        for (const item of res.data.data.result) {
          objectCnt += parseInt(item.value[1])
        }
        setObjects(objectCnt)
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_cluster_nodes_online_total',
        time: new Date()
      },
      token
    )
      .then((res) => {
        const onlineServer = parseInt(res.data.data.result[0].value[1])
        getQuery(
          {
            query: 'minio_cluster_nodes_offline_total',
            time: new Date()
          },
          token
        )
          .then((res) => {
            const offlineServer = parseInt(res.data.data.result[0].value[1])
            setServers({
              online: onlineServer,
              offline: offlineServer
            })
          })
          .catch((err) => {
            console.log(err)
          })
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_cluster_disk_online_total',
        time: new Date()
      },
      token
    )
      .then((res) => {
        const onlineDisk = parseInt(res.data.data.result[0].value[1])
        getQuery(
          {
            query: 'minio_cluster_disk_offline_total',
            time: new Date()
          },
          token
        )
          .then((res) => {
            const offlineDisk = parseInt(res.data.data.result[0].value[1])
            setDrives({
              online: onlineDisk,
              offline: offlineDisk
            })
          })
          .catch((err) => {
            console.log(err)
          })
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_cluster_capacity_usable_free_bytes',
        time: new Date()
      },
      token
    )
      .then((res) => {
        const freeCapacity = parseInt(res.data.data.result[0].value[1])
        getQuery(
          {
            query: 'minio_cluster_capacity_usable_total_bytes',
            time: new Date()
          },
          token
        )
          .then((res) => {
            const totalCapacity = parseInt(res.data.data.result[0].value[1])
            setCapacity({
              free: freeCapacity,
              total: totalCapacity
            })
            setCapacityOption({
              tooltip: {
                trigger: 'item'
              },
              series: [
                {
                  name: 'Capacity',
                  type: 'pie',
                  radius: ['40%', '70%'],
                  avoidLabelOverlap: false,
                  label: {
                    show: false,
                    position: 'center'
                  },
                  labelLine: {
                    show: false
                  },
                  data: [
                    {
                      value: ((totalCapacity - freeCapacity) / Math.pow(1024, 4)).toFixed(2),
                      name: 'Used'
                    },
                    { value: (freeCapacity / Math.pow(1024, 4)).toFixed(2), name: 'Free' }
                  ]
                }
              ]
            })
          })
          .catch((err) => {
            console.log(err)
          })
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_s3_traffic_sent_bytes',
        time: new Date()
      },
      token
    )
      .then((res) => {
        const networkGet =
          parseInt(res.data.data.result[0].value[1]) + parseInt(res.data.data.result[1].value[1])
        getQuery(
          {
            query: 'minio_s3_traffic_received_bytes',
            time: new Date()
          },
          token
        )
          .then((res) => {
            const networkPut =
              parseInt(res.data.data.result[0].value[1]) +
              parseInt(res.data.data.result[1].value[1])
            setNetwork({
              get: networkGet,
              put: networkPut
            })
          })
          .catch((err) => {
            console.log(err)
          })
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_heal_time_last_activity_nano_seconds',
        time: new Date()
      },
      token
    )
      .then((res) => {
        // console.log(res)
        const ns = parseInt(res.data.data.result[0].value[1])
        const days = Math.floor(ns / Math.pow(10, 9) / 3600 / 24)
        setTimeSinceLastHealActivity(days)
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_usage_last_activity_nano_seconds',
        time: new Date()
      },
      token
    )
      .then((res) => {
        // console.log(res)
        const ns = parseInt(res.data.data.result[0].value[1])
        const days = Math.floor(ns / Math.pow(10, 9) / 3600 / 24)
        setTimeSinceLastScanActivity(days)
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_node_process_uptime_seconds',
        time: new Date()
      },
      token
    )
      .then((res) => {
        // console.log(res)
        const seconds = parseInt(res.data.data.result[0].value[1])
        const days = Math.floor(seconds / 3600 / 24)
        setUptime(days)
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_bucket_usage_total_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        let xAxisData: any[] = []
        let yAxisData: any[] = []
        for (let i = 0; i < res.data.data.result[0].values.length; i++) {
          const item = res.data.data.result[0].values[i]
          xAxisData.push(parseInt(item[0]))
          let curYAxisValue = 0
          for (let j = 0; j < res.data.data.result.length; j++) {
            curYAxisValue += parseInt(res.data.data.result[j].values[i][1])
          }
          yAxisData.push(curYAxisValue)
        }
        xAxisData = xAxisData.map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        yAxisData = yAxisData.map((item) => item / Math.pow(1024, 4))
        setDataUsageOption({
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          tooltip: {
            trigger: 'axis'
          },
          series: [
            {
              data: yAxisData,
              type: 'line',
              areaStyle: {}
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQuery(
      {
        query: 'minio_bucket_objects_size_distribution',
        time: new Date()
      },
      token
    )
      .then((res) => {
        const range_value = {}
        for (let i = 0; i < res.data.data.result.length; i++) {
          const item = res.data.data.result[i]
          if (!(item.metric.range in range_value)) range_value[item.metric.range] = 0
          range_value[item.metric.range] += parseInt(item.value[1])
        }
        setObjectSizeOption({
          yAxis: {
            type: 'category',
            data: [
              '< 1024B',
              '1024B - 1MB',
              '1MB - 10MB',
              '10MB - 64MB',
              '64MB - 128MB',
              '128MB - 512MB',
              '> 512MB'
            ].reverse()
          },
          xAxis: {
            type: 'value'
          },
          tooltip: {
            trigger: 'axis'
          },
          series: [
            {
              data: [
                range_value['LESS_THAN_1024_B'],
                range_value['BETWEEN_1024_B_AND_1_MB'],
                range_value['BETWEEN_1_MB_AND_10_MB'],
                range_value['BETWEEN_10_MB_AND_64_MB'],
                range_value['BETWEEN_64_MB_AND_128_MB'],
                range_value['BETWEEN_128_MB_AND_512_MB'],
                range_value['GREATER_THAN_512_MB']
              ].reverse(),
              type: 'bar'
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_s3_traffic_received_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        let xAxisData: any[] = []
        let yAxisData_1: any[] = []
        let yAxisData_2: any[] = []
        res.data.data.result[0].values.forEach((element) => {
          xAxisData.push(element[0])
          yAxisData_1.push(parseInt(element[1]))
        })
        res.data.data.result[1].values.forEach((element) => {
          yAxisData_2.push(parseInt(element[1]))
        })
        xAxisData = xAxisData.map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        yAxisData_1 = yAxisData_1.map((item) => item / Math.pow(1024, 4))
        yAxisData_2 = yAxisData_2.map((item) => item / Math.pow(1024, 4))
        setDataReceiveOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['Data Received [192.168.1.2:9000]', 'Data Received [192.168.1.3:9000]'],
            top: '20px'
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: 'Data Received [192.168.1.2:9000]',
              type: 'line',
              data: yAxisData_1
            },
            {
              name: 'Data Received [192.168.1.3:9000]',
              type: 'line',
              data: yAxisData_2
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_s3_traffic_sent_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        let xAxisData: any[] = []
        let yAxisData_1: any[] = []
        let yAxisData_2: any[] = []
        res.data.data.result[0].values.forEach((element) => {
          xAxisData.push(element[0])
          yAxisData_1.push(parseInt(element[1]))
        })
        res.data.data.result[1].values.forEach((element) => {
          yAxisData_2.push(parseInt(element[1]))
        })
        xAxisData = xAxisData.map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        yAxisData_1 = yAxisData_1.map((item) => item / Math.pow(1024, 4))
        yAxisData_2 = yAxisData_2.map((item) => item / Math.pow(1024, 4))
        setDataSendOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['Data Sent [192.168.1.2:9000]', 'Data Sent [192.168.1.3:9000]'],
            top: '20px'
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: 'Data Sent [192.168.1.2:9000]',
              type: 'line',
              data: yAxisData_1
            },
            {
              name: 'Data Sent [192.168.1.3:9000]',
              type: 'line',
              data: yAxisData_2
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_s3_requests_total',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        const xAxisDataSet = new Set<number>()
        const yAxisData: any[] = []
        const names: any[] = []
        for (let i = 0; i < res.data.data.result.length; i++) {
          const item = res.data.data.result[i]
          const curYAxisData: any[] = []
          names.push(item.metric.server + ', ' + item.metric.api)
          for (let j = 0; j < item.values.length; j++) {
            xAxisDataSet.add(item.values[j][0])
            curYAxisData.push(item.values[j][1])
          }
          yAxisData.push(curYAxisData)
        }
        const xAxisData = Array.from(xAxisDataSet).map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        const series = yAxisData.map((item, index) => {
          return {
            name: names[index],
            type: 'line',
            data: item
          }
        })
        setApiRequestOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: names,
            type: 'scroll',
            top: '10px',
            formatter: (name) => {
              if (name.length > 10) name = name.slice(0, 10) + '...'
            },
            tooltip: {
              show: true
            }
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: series
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_s3_requests_errors_total',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        const xAxisDataSet = new Set<number>()
        const yAxisData: any[] = []
        const names: any[] = []
        for (let i = 0; i < res.data.data.result.length; i++) {
          const item = res.data.data.result[i]
          const curYAxisData: any[] = []
          names.push(item.metric.server + ', ' + item.metric.api)
          for (let j = 0; j < item.values.length; j++) {
            xAxisDataSet.add(item.values[j][0])
            curYAxisData.push(item.values[j][1])
          }
          yAxisData.push(curYAxisData)
        }
        const xAxisData = Array.from(xAxisDataSet).map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        const series = yAxisData.map((item, index) => {
          return {
            name: names[index],
            type: 'line',
            data: item
          }
        })
        setApiRequestErrorOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: names,
            type: 'scroll',
            top: '18px',
            width: '80%',
            formatter: (name) => {
              if (name.length > 10) name = name.slice(0, 10) + '...'
            },
            tooltip: {
              show: true
            }
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: series
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_inter_node_traffic_received_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    ).then((res) => {
      let xAxisData: any[] = []
      const yAxisData_1: any[] = []
      const yAxisData_2: any[] = []
      res.data.data.result[0].values.forEach((element) => {
        xAxisData.push(element[0])
        yAxisData_1.push(parseInt(element[1]))
      })
      res.data.data.result[1].values.forEach((element) => {
        yAxisData_2.push(parseInt(element[1]))
      })
      xAxisData = xAxisData.map((item) => {
        const date = new Date(item * 1000)
        const year = date.getFullYear() + '-'
        const month =
          (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
        const day = date.getDate() + ' '
        return year + month + day
      })

      getQueryRange(
        {
          query: 'minio_inter_node_traffic_sent_bytes',
          start: startDate / 1000,
          end: endDate / 1000,
          step: 5000
        },
        token
      )
        .then((res) => {
          const yAxisData_3: any[] = []
          const yAxisData_4: any[] = []
          res.data.data.result[0].values.forEach((element) => {
            yAxisData_3.push(parseInt(element[1]))
          })
          res.data.data.result[1].values.forEach((element) => {
            yAxisData_4.push(parseInt(element[1]))
          })

          setInternodeDataOption({
            tooltip: {
              trigger: 'axis'
            },
            legend: {
              data: [
                'Internode Bytes Received [192.168.1.2:9000]',
                'Internode Bytes Received [192.168.1.3:9000]',
                'Internode Bytes Sent [192.168.1.2:9000]',
                'Internode Bytes Sent [192.168.1.3:9000]'
              ],
              top: '18px',
              formatter: (name) => {
                if (name.length > 10) name = name.slice(0, 10) + '...'
              },
              tooltip: {
                show: true
              }
            },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: xAxisData
            },
            yAxis: {
              type: 'value'
            },
            series: [
              {
                name: 'Internode Bytes Received [192.168.1.2:9000]',
                type: 'line',
                data: yAxisData_1
              },
              {
                name: 'Internode Bytes Received [192.168.1.3:9000]',
                type: 'line',
                data: yAxisData_2
              },
              {
                name: 'Internode Bytes Sent [192.168.1.2:9000]',
                type: 'line',
                data: yAxisData_3
              },
              {
                name: 'Internode Bytes Sent [192.168.1.3:9000]',
                type: 'line',
                data: yAxisData_4
              }
            ]
          })
        })
        .catch((err) => {
          console.log(err)
        })
    })

    getQueryRange(
      {
        query: 'minio_node_io_rchar_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    ).then((res) => {
      let xAxisData: any[] = []
      const yAxisData_1: any[] = []
      const yAxisData_2: any[] = []
      res.data.data.result[0].values.forEach((element) => {
        xAxisData.push(element[0])
        yAxisData_1.push(parseInt(element[1]))
      })
      res.data.data.result[1].values.forEach((element) => {
        yAxisData_2.push(parseInt(element[1]))
      })
      xAxisData = xAxisData.map((item) => {
        const date = new Date(item * 1000)
        const year = date.getFullYear() + '-'
        const month =
          (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
        const day = date.getDate() + ' '
        return year + month + day
      })

      getQueryRange(
        {
          query: 'minio_node_io_wchar_bytes',
          start: startDate / 1000,
          end: endDate / 1000,
          step: 5000
        },
        token
      )
        .then((res) => {
          const yAxisData_3: any[] = []
          const yAxisData_4: any[] = []
          res.data.data.result[0].values.forEach((element) => {
            yAxisData_3.push(parseInt(element[1]))
          })
          res.data.data.result[1].values.forEach((element) => {
            yAxisData_4.push(parseInt(element[1]))
          })

          setNodeIOOption({
            tooltip: {
              trigger: 'axis'
            },
            legend: {
              data: [
                'Node RChar [192.168.1.2:9000]',
                'Node RChar [192.168.1.3:9000]',
                'Node WChar [192.168.1.2:9000]',
                'Node WChar [192.168.1.3:9000]'
              ],
              top: '10px'
            },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: xAxisData
            },
            yAxis: {
              type: 'value'
            },
            series: [
              {
                name: 'Node RChar [192.168.1.2:9000]',
                type: 'line',
                data: yAxisData_1
              },
              {
                name: 'Node RChar [192.168.1.3:9000]',
                type: 'line',
                data: yAxisData_2
              },
              {
                name: 'Node WChar [192.168.1.2:9000]',
                type: 'line',
                data: yAxisData_3
              },
              {
                name: 'Node WChar [192.168.1.3:9000]',
                type: 'line',
                data: yAxisData_4
              }
            ]
          })
        })
        .catch((err) => {
          console.log(err)
        })
    })

    getQueryRange(
      {
        query: 'minio_node_process_resident_memory_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        let xAxisData: any[] = []
        const yAxisData_1: any[] = []
        const yAxisData_2: any[] = []
        res.data.data.result[0].values.forEach((element) => {
          xAxisData.push(element[0])
          yAxisData_1.push(parseInt(element[1]))
        })
        res.data.data.result[1].values.forEach((element) => {
          yAxisData_2.push(parseInt(element[1]))
        })
        xAxisData = xAxisData.map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        setNodeMemoryOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['Memory Used [192.168.1.2:9000]', 'Memory Used [192.168.1.3:9000]'],
            top: '10px'
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: 'Memory Used [192.168.1.2:9000]',
              type: 'line',
              data: yAxisData_1
            },
            {
              name: 'Memory Used [192.168.1.3:9000]',
              type: 'line',
              data: yAxisData_2
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_node_process_cpu_total_seconds',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        let xAxisData: any[] = []
        const yAxisData_1: any[] = []
        const yAxisData_2: any[] = []
        res.data.data.result[0].values.forEach((element) => {
          xAxisData.push(element[0])
          yAxisData_1.push(parseInt(element[1]))
        })
        res.data.data.result[1].values.forEach((element) => {
          yAxisData_2.push(parseInt(element[1]))
        })
        xAxisData = xAxisData.map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        setNodeCPUOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['CPU Usage Rate [192.168.1.2:9000]', 'CPU Usage Rate [192.168.1.3:9000]'],
            top: '10px'
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: 'CPU Usage Rate [192.168.1.2:9000]',
              type: 'line',
              data: yAxisData_1
            },
            {
              name: 'CPU Usage Rate [192.168.1.3:9000]',
              type: 'line',
              data: yAxisData_2
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_node_disk_free_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        const xAxisDataSet = new Set<number>()
        const yAxisData: any[] = []
        const names: any[] = []
        for (let i = 0; i < res.data.data.result.length; i++) {
          const item = res.data.data.result[i]
          const curYAxisData: any[] = []
          names.push(item.metric.server + item.metric.disk)
          for (let j = 0; j < item.values.length; j++) {
            xAxisDataSet.add(item.values[j][0])
            curYAxisData.push(item.values[j][1])
          }
          yAxisData.push(curYAxisData)
        }
        const xAxisData = Array.from(xAxisDataSet).map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        const series = yAxisData.map((item, index) => {
          return {
            name: names[index],
            type: 'line',
            data: item
          }
        })
        setDriveFreeOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: names,
            type: 'scroll',
            top: '10px',
            formatter: (name) => {
              if (name.length > 10) name = name.slice(0, 10) + '...'
            },
            tooltip: {
              show: true
            }
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: series
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_node_disk_used_bytes',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        const xAxisDataSet = new Set<number>()
        const yAxisData: any[] = []
        const names: any[] = []
        for (let i = 0; i < res.data.data.result.length; i++) {
          const item = res.data.data.result[i]
          const curYAxisData: any[] = []
          names.push(item.metric.server + item.metric.disk)
          for (let j = 0; j < item.values.length; j++) {
            xAxisDataSet.add(item.values[j][0])
            curYAxisData.push(item.values[j][1])
          }
          yAxisData.push(curYAxisData)
        }
        const xAxisData = Array.from(xAxisDataSet).map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        const series = yAxisData.map((item, index) => {
          return {
            name: names[index],
            type: 'line',
            data: item
          }
        })
        setDrivesUsedOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: names,
            type: 'scroll',
            top: '10px',
            formatter: (name) => {
              if (name.length > 10) name = name.slice(0, 10) + '...'
            },
            tooltip: {
              show: true
            }
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: series
        })
      })
      .catch((err) => {
        console.log(err)
      })

    getQueryRange(
      {
        query: 'minio_node_syscall_read_total',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    ).then((res) => {
      let xAxisData: any[] = []
      const yAxisData_1: any[] = []
      const yAxisData_2: any[] = []
      res.data.data.result[0].values.forEach((element) => {
        xAxisData.push(element[0])
        yAxisData_1.push(parseInt(element[1]))
      })
      res.data.data.result[1].values.forEach((element) => {
        yAxisData_2.push(parseInt(element[1]))
      })
      xAxisData = xAxisData.map((item) => {
        const date = new Date(item * 1000)
        const year = date.getFullYear() + '-'
        const month =
          (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
        const day = date.getDate() + ' '
        return year + month + day
      })

      getQueryRange(
        {
          query: 'minio_node_syscall_write_total',
          start: startDate / 1000,
          end: endDate / 1000,
          step: 5000
        },
        token
      )
        .then((res) => {
          const yAxisData_3: any[] = []
          const yAxisData_4: any[] = []
          res.data.data.result[0].values.forEach((element) => {
            yAxisData_3.push(parseInt(element[1]))
          })
          res.data.data.result[1].values.forEach((element) => {
            yAxisData_4.push(parseInt(element[1]))
          })

          setNodeSyscallsOption({
            tooltip: {
              trigger: 'axis'
            },
            legend: {
              data: [
                'Read Syscalls [192.168.1.2:9000]',
                'Read Syscalls [192.168.1.3:9000]',
                'Write Syscalls [192.168.1.2:9000]',
                'Write Syscalls [192.168.1.3:9000]'
              ],
              top: '10px'
            },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: xAxisData
            },
            yAxis: {
              type: 'value'
            },
            series: [
              {
                name: 'Read Syscalls [192.168.1.2:9000]',
                type: 'line',
                data: yAxisData_1
              },
              {
                name: 'Read Syscalls [192.168.1.3:9000]',
                type: 'line',
                data: yAxisData_2
              },
              {
                name: 'Write Syscalls [192.168.1.2:9000]',
                type: 'line',
                data: yAxisData_3
              },
              {
                name: 'Write Syscalls [192.168.1.3:9000]',
                type: 'line',
                data: yAxisData_4
              }
            ]
          })
        })
        .catch((err) => {
          console.log(err)
        })
    })

    getQueryRange(
      {
        query: 'minio_node_file_descriptor_open_total',
        start: startDate / 1000,
        end: endDate / 1000,
        step: 5000
      },
      token
    )
      .then((res) => {
        let xAxisData: any[] = []
        const yAxisData_1: any[] = []
        const yAxisData_2: any[] = []
        res.data.data.result[0].values.forEach((element) => {
          xAxisData.push(element[0])
          yAxisData_1.push(parseInt(element[1]))
        })
        res.data.data.result[1].values.forEach((element) => {
          yAxisData_2.push(parseInt(element[1]))
        })
        xAxisData = xAxisData.map((item) => {
          const date = new Date(item * 1000)
          const year = date.getFullYear() + '-'
          const month =
            (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1) + '-'
          const day = date.getDate() + ' '
          return year + month + day
        })
        setNodeFileOption({
          tooltip: {
            trigger: 'axis'
          },
          legend: {
            data: ['Open FDs [192.168.1.2:9000]', 'Open FDs [192.168.1.3:9000]'],
            top: '10px'
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: 'Open FDs [192.168.1.2:9000]',
              type: 'line',
              data: yAxisData_1
            },
            {
              name: 'Open FDs [192.168.1.3:9000]',
              type: 'line',
              data: yAxisData_2
            }
          ]
        })
      })
      .catch((err) => {
        console.log(err)
      })
  }

  useEffect(() => {
    getMinIOData()
  }, [])

  const rangeValue = useMemo<[Dayjs, Dayjs]>(
    () => [dayjs(startDate), dayjs(endDate)],
    [startDate, endDate]
  )

  const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (!dates?.[0] || !dates?.[1]) return
    setStartDate(dates[0].valueOf())
    setEndDate(dates[1].valueOf())
  }

  return (
    <>
      <Tabs defaultActiveKey='1' destroyInactiveTabPane>
        <Tabs.TabPane tab='使用情况' key='1'>
          <Row>
            <Col style={{ marginTop: 5 }} span={1}>
              过滤：
            </Col>
            <Col span={19}>
              <RangePicker
                value={rangeValue}
                onChange={handleRangeChange}
                style={{ width: '100%' }}
                showTime
              />
            </Col>
            <Col span={2} offset={1}>
              <Button style={{ backgroundColor: 'rgb(84, 73, 43)', width: '100%' }} type='primary'>
                同步数据
              </Button>
            </Col>
          </Row>
          <Row>
            <Col span={5}>
              <div className='dashboard-block'>
                <svg
                  className='dashboard-block-icon'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <g>
                    <path d='M244.1,8.4c-3.9-5.3-10.1-8.5-16.7-8.5H21.6C15,0,8.8,3.1,4.9,8.4C0.8,14-0.9,21,0.3,27.9 c5.1,29.6,15.8,91.9,24.3,141.7v0.1C29,195,32.8,217.1,35,229.9c1.4,10.8,10.4,18.9,21.3,19.3h136.5 c10.9-0.4,19.9-8.5,21.3-19.3l10.3-60.1l0.1-0.4L238.4,88v-0.2l10.3-59.9C249.9,21,248.3,14,244.1,8.4 M206.1,177h-163 l-3.2-18.6h169.3L206.1,177z M220,95.3H28.9l-3.2-18.6h197.4L220,95.3z'></path>
                  </g>
                </svg>
                <div className='dashboard-block-title'>数据容器</div>
                <div className='dashboard-block-text'>{buckets}</div>
              </div>
            </Col>
            <Col span={5} offset={1}>
              <div className='dashboard-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='dashboard-block-icon'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g data-name='Total Objects' clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <path
                      data-name='total-objects-icn'
                      d='M-.004 128.002a128.148 128.148 0 0 1 128-128 128.148 128.148 0 0 1 128 128 128.144 128.144 0 0 1-128 128 128.144 128.144 0 0 1-128-128Zm19.844 0a108.275 108.275 0 0 0 108.156 108.155 108.28 108.28 0 0 0 108.16-108.155 108.283 108.283 0 0 0-108.16-108.157A108.278 108.278 0 0 0 19.842 128.002Zm27.555 31.581a37.6 37.6 0 0 1 37.564-37.565 37.608 37.608 0 0 1 37.561 37.565 37.609 37.609 0 0 1-37.561 37.565 37.606 37.606 0 0 1-37.563-37.566Zm108.127 34.939a17.425 17.425 0 0 1-17.408-17.4v-37.7a17.429 17.429 0 0 1 17.408-17.407h37.689a17.429 17.429 0 0 1 17.408 17.407v37.7a17.425 17.425 0 0 1-17.408 17.4Zm-54.881-81.311a13.3 13.3 0 0 1-11.477-6.625 13.3 13.3 0 0 1 0-13.249l26.861-46.521a13.287 13.287 0 0 1 11.477-6.629 13.281 13.281 0 0 1 11.475 6.629l26.861 46.521a13.285 13.285 0 0 1 0 13.249 13.294 13.294 0 0 1-11.479 6.625Z'
                      stroke='rgba(0,0,0,0)'
                      stroke-miterlimit='10'
                    ></path>
                    <path data-name='Rect\xE1ngulo 853' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='dashboard-block-title'>对象数量</div>
                <div className='dashboard-block-text'>
                  {(objects / Math.pow(1000, 2)).toFixed(2)}M
                </div>
              </div>
            </Col>
            <Col span={5} offset={1}>
              <div className='dashboard-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='dashboard-block-icon'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <g data-name='servers-icn'>
                      <path
                        data-name='Trazado 404'
                        d='M128 0C64.408 0 0 15.267 0 44.414v167.17c0 29.147 64.408 44.415 128 44.415s128-15.268 128-44.415V44.414C256 15.267 191.592 0 128 0Zm105.743 211.584c0 8.945-37.324 25.909-105.739 25.909s-105.74-17.118-105.74-25.909v-58.911c24.116 11.967 65.15 18.2 105.74 18.2s81.623-6.169 105.739-18.29Zm0-85.128c0 8.791-37.324 25.908-105.739 25.908s-105.74-17.118-105.74-25.908V70.537c24.116 12.06 65.15 18.29 105.74 18.29s81.623-6.168 105.739-18.29ZM128.004 70.321c-68.416 0-105.74-17.118-105.74-25.908s37.324-25.908 105.74-25.908 105.739 17.119 105.739 25.909S196.415 70.323 128 70.323Z'
                      ></path>
                      <circle
                        data-name='Elipse 59'
                        cx='15.793'
                        cy='15.793'
                        r='15.793'
                        transform='rotate(-31.72 348.405 44.732)'
                      ></circle>
                      <circle
                        data-name='Elipse 60'
                        cx='15.793'
                        cy='15.793'
                        r='15.793'
                        transform='rotate(-31.72 207.061 4.576)'
                      ></circle>
                    </g>
                  </g>
                </svg>
                <div className='dashboard-block-title'>服务器</div>
                <div className='online-text'>{servers.online}</div>
                <div className='offline-text'>{servers.offline}</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='online-icon'
                  fill='rgb(95, 193, 139)'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <circle data-name='circle-icn' cx='128' cy='128' r='128'></circle>
                    <path data-name='Rect\xE1ngulo 852' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='online-legend'>在线</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='offline-icon'
                  fill='rgb(181, 62, 72)'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <circle data-name='circle-icn' cx='128' cy='128' r='128'></circle>
                    <path data-name='Rect\xE1ngulo 852' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='offline-legend'>离线</div>
              </div>
            </Col>
            <Col span={5} offset={1}>
              <div className='dashboard-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='dashboard-block-icon'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <path
                      data-name='Trazado 423'
                      d='M34.549 188.281h186.9a6.641 6.641 0 1 1 0 13.282h-186.9a6.641 6.641 0 0 1-6.641-6.641 6.641 6.641 0 0 1 6.641-6.641Z'
                    ></path>
                    <path
                      data-name='Trazado 425'
                      d='M38.567 162.693a10.385 10.385 0 1 1-10.385 10.385 10.385 10.385 0 0 1 10.385-10.385Z'
                    ></path>
                    <path
                      data-name='Trazado 424'
                      d='M66.709 162.83a10.384 10.384 0 1 1-8.588 11.911 10.384 10.384 0 0 1 8.588-11.912Z'
                    ></path>
                    <path
                      data-name='Trazado 405'
                      d='M255.699 154.149a37.6 37.6 0 0 0-2.994-12.568l-41.95-104.219C207.537 29.62 199.33 24 191.241 24H64.759c-8.089 0-16.3 5.62-19.514 13.362L3.295 141.581a37.61 37.61 0 0 0-2.994 12.568 22.107 22.107 0 0 0-.3 3.612v51.4a22.089 22.089 0 0 0 22.065 22.064h211.87a22.09 22.09 0 0 0 22.065-22.064v-51.4a22.134 22.134 0 0 0-.302-3.612ZM65.754 46.413h124.491l36.053 89.283H30.013Zm167.833 162.4H22.412v-50.708h211.175Z'
                    ></path>
                    <path data-name='Rect\xE1ngulo 855' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='dashboard-block-title'>硬盘</div>
                <div className='online-text'>{drives.online}</div>
                <div className='offline-text'>{drives.offline}</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='online-icon'
                  fill='rgb(95, 193, 139)'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <circle data-name='circle-icn' cx='128' cy='128' r='128'></circle>
                    <path data-name='Rect\xE1ngulo 852' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='online-legend'>在线</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='offline-icon'
                  fill='rgb(181, 62, 72)'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <circle data-name='circle-icn' cx='128' cy='128' r='128'></circle>
                    <path data-name='Rect\xE1ngulo 852' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='offline-legend'>离线</div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>使用率</div>
                <ReactECharts
                  option={capacityOption}
                  autoResize={true}
                  className='echarts-style-0'
                />
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='dashboard-block-icon'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g data-name='Reported Usage' clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <path
                      data-name='Trazado 390'
                      d='M128.003 0a128.151 128.151 0 0 0-128 128c0 70.573 57.424 127.995 128 127.995a128.147 128.147 0 0 0 128-127.995 128.15 128.15 0 0 0-128-128Zm0 223.078a95.188 95.188 0 0 1-95.085-95.075 95.191 95.191 0 0 1 95.085-95.084v95.084h95.075a95.184 95.184 0 0 1-95.075 95.074Z'
                    ></path>
                    <path data-name='Rect\xE1ngulo 869' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='used-text'>已使用</div>
                <div className='used-data'>
                  {((capacity.total - capacity.free) / Math.pow(1024, 4)).toFixed(2)}TiB
                </div>
                <div className='total-text'>
                  总容量： {(capacity.total / Math.pow(1024, 4)).toFixed(2)}TiB
                </div>
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>网络</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='dashboard-block-icon'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <g data-name='SpeedTestIcon'>
                      <path data-name='Rect\xE1ngulo 850' fill='none' d='M0 0h256v256H0z'></path>
                      <path
                        data-name='Trazado 426'
                        d='m63.413 63.057-.1.084a5.326 5.326 0 0 0 3.505 9.344l-.011.063a5.319 5.319 0 0 0 3.516-1.371l.1-.084q.167-.135.322-.281a5.337 5.337 0 1 0-7.333-7.756Z'
                      ></path>
                      <path
                        data-name='Trazado 427'
                        d='M48.827 88.433a4.336 4.336 0 0 0-5.884 1.729v.095a4.336 4.336 0 0 0 3.817 6.344l-.011.01a4.361 4.361 0 0 0 2.078-8.178Z'
                      ></path>
                      <path
                        data-name='Trazado 428'
                        d='M127.29 52.816h.293a7.816 7.816 0 1 0-.046-15.631h-.247a7.816 7.816 0 0 0 0 15.631Z'
                      ></path>
                      <path
                        data-name='Trazado 429'
                        d='M37.263 119.721h-.028a2.958 2.958 0 0 0-3.324 2.541v.08a2.973 2.973 0 0 0 2.559 3.336 3.173 3.173 0 0 0 .379 0l-.021.007a2.972 2.972 0 0 0 2.959-2.558v-.056a2.966 2.966 0 0 0-2.524-3.35Z'
                      ></path>
                      <path
                        data-name='Trazado 430'
                        d='m91.954 44.052-.209.078a7.07 7.07 0 0 0 2.5 13.688l-.022.065a7.009 7.009 0 0 0 2.537-.529l.165-.066.1-.039a7.071 7.071 0 1 0-5.076-13.2Z'
                      ></path>
                      <path
                        data-name='Trazado 431'
                        d='M192.48 73.763a9.817 9.817 0 0 0-.929-13.852l-.268-.235a9.817 9.817 0 0 0-12.881 14.8l.246.212a9.806 9.806 0 0 0 6.452 2.426 9.815 9.815 0 0 0 7.38-3.351Z'
                      ></path>
                      <path data-name='Trazado 432' d='M205.131 108.033Z'></path>
                      <path
                        data-name='Trazado 433'
                        d='m227.69 121.128-.067-.495a12.786 12.786 0 0 0-12.612-11.007 12.761 12.761 0 0 0-12.638 14.485v.428a12.786 12.786 0 0 0 12.612 11.047 13.068 13.068 0 0 0 1.778-.12 12.76 12.76 0 0 0 10.927-14.338Z'
                      ></path>
                      <path
                        data-name='Trazado 434'
                        d='M210.416 102.215a11.283 11.283 0 0 0 4.537-15.3l-.2-.361a16.398 16.398 0 0 0-.27-.5 11.283 11.283 0 1 0-19.545 11.281l.187.336a11.278 11.278 0 0 0 15.289 4.538Z'
                      ></path>
                      <path
                        data-name='Trazado 435'
                        d='m160.575 42.633-.289-.111a8.657 8.657 0 1 0-6.052 16.222l.255.1a8.643 8.643 0 0 0 3.048.556l-.01.066a8.7 8.7 0 0 0 3.048-16.833Z'
                      ></path>
                      <path
                        data-name='Trazado 436'
                        d='m148.433 112.148-13.839 11.867a.333.333 0 0 1-.331 0 17.171 17.171 0 1 0 10.435 12.167.333.333 0 0 1 0-.316l13.9-11.866a7.807 7.807 0 0 0-10.165-11.851Zm-12.039 27.588a8.26 8.26 0 1 1-8.26-8.26 8.26 8.26 0 0 1 8.26 8.259Z'
                      ></path>
                      <path
                        data-name='Trazado 437'
                        d='M138.134 194.756h-20.3a3.765 3.765 0 0 0 0 7.53h20.33a3.764 3.764 0 0 0 3.764-3.765v-.03a3.765 3.765 0 0 0-3.794-3.735Z'
                      ></path>
                      <path
                        data-name='Trazado 438'
                        d='M127.999 0a128 128 0 1 0 128 128 128.15 128.15 0 0 0-128-128Zm0 233.412A105.412 105.412 0 1 1 233.414 128a105.412 105.412 0 0 1-105.415 105.412Z'
                      ></path>
                    </g>
                  </g>
                </svg>
                <div className='upload-text'>已上传</div>
                <div className='download-text'>已下载</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='upload-icon'
                  fill='rgb(95, 193, 139)'
                  viewBox='0 0 15 15'
                >
                  <path
                    d='M7.5,0h0A7.5,7.5,0,0,0,0,7.5H0A7.5,7.5,0,0,0,7.5,15h0a7.5,7.5,0,0,0,0-15M9.978,9.776l-1.9,1.9a.819.819,0,0,1-1.166,0h0L5.022,9.776a.773.773,0,0,1-.186-.864.875.875,0,0,1,.779-.541.793.793,0,0,1,.565.247l.5.5V3.9a.818.818,0,0,1,1.636,0V9.119l.5-.5a.79.79,0,0,1,.564-.248.872.872,0,0,1,.779.541.772.772,0,0,1-.185.864'
                    transform='translate(15 15) rotate(180)'
                  ></path>
                </svg>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='download-icon'
                  fill='rgb(51, 116, 164)'
                  viewBox='0 0 15 15'
                >
                  <path d='M7.5,0h0A7.5,7.5,0,0,0,0,7.5H0A7.5,7.5,0,0,0,7.5,15h0a7.5,7.5,0,0,0,0-15M9.978,9.776l-1.9,1.9a.819.819,0,0,1-1.166,0h0L5.023,9.776a.773.773,0,0,1-.186-.864.875.875,0,0,1,.779-.541.793.793,0,0,1,.565.247l.5.5V3.9a.818.818,0,0,1,1.636,0V9.119l.5-.5a.79.79,0,0,1,.564-.248.872.872,0,0,1,.779.541.772.772,0,0,1-.185.864'></path>
                </svg>
                <div className='upload-data'>{(network.get / Math.pow(1024, 3)).toFixed(2)}GiB</div>
                <div className='download-data'>
                  {(network.put / Math.pow(1024, 3)).toFixed(2)}GiB
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={7}>
              <div className='small-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='small-block-icon-style'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <path
                      data-name='heal-icn'
                      d='m157.546 239.085-4.729-4.747-24.864-24.809-29.5 29.555a57.519 57.519 0 0 1-81.482 0 57.729 57.729 0 0 1 0-81.769l29.5-29.547-24.864-24.927-4.635-4.629a57.738 57.738 0 0 1 0-81.777c21.764-21.913 59.749-21.913 81.482 0l4.729 4.645 24.77 24.911 29.593-29.555c21.764-21.913 59.718-21.913 81.482 0a57.738 57.738 0 0 1 0 81.777l-29.5 29.555 24.864 24.793 4.635 4.755a57.718 57.718 0 1 1-81.482 81.769Zm13.654-23.036 4.572 4.629c12.15 12.028 33.006 12.028 45.031 0a31.967 31.967 0 0 0 0-44.957l-4.791-4.747ZM35.261 175.721a31.814 31.814 0 0 0 0 44.957c11.962 12.028 32.943 12.028 44.968 0l29.471-29.547-45-45.09Zm22.954-72.88 6.482 6.52 81.638 81.769 6.482 6.5 44.968-45.074-6.482-6.52-81.603-81.63-6.514-6.52Zm117.556-67.992-29.436 29.557 44.965 44.955 29.5-29.555a31.6 31.6 0 0 0 9.238-22.541 31.28 31.28 0 0 0-9.238-22.416 32.381 32.381 0 0 0-45.031 0Zm-140.51 0a31.211 31.211 0 0 0-9.3 22.416 31.525 31.525 0 0 0 9.3 22.541l4.729 4.762 44.843-45.09-4.6-4.629a31.61 31.61 0 0 0-44.968 0Zm105.562 118.465a12.731 12.731 0 1 1 12.746 12.892 12.816 12.816 0 0 1-12.746-12.892Zm-25.616-25.546a12.808 12.808 0 1 1 12.745 12.773 12.747 12.747 0 0 1-12.744-12.773Zm-25.49-25.679a12.746 12.746 0 1 1 12.714 12.9 12.8 12.8 0 0 1-12.714-12.901Z'
                    ></path>
                    <path
                      data-name='heal-icn - Contorno'
                      d='M198.286 256.5a57.755 57.755 0 0 1-41.094-17.062l-4.729-4.747-24.509-24.455-29.146 29.2a57.907 57.907 0 0 1-82.189 0A57.353 57.353 0 0 1 3.9 220.544a58.292 58.292 0 0 1-4.4-22.407 57.536 57.536 0 0 1 17.121-41.177l29.144-29.192-24.512-24.573-4.634-4.629a58.238 58.238 0 0 1 0-82.486A54.985 54.985 0 0 1 35.647 3.644 59.5 59.5 0 0 1 46.5.536a61.384 61.384 0 0 1 22.457 0A59.431 59.431 0 0 1 79.8 3.644a54.885 54.885 0 0 1 19.007 12.437l4.73 4.646 24.417 24.555 29.238-29.2a54.994 54.994 0 0 1 19.023-12.438A59.465 59.465 0 0 1 187.061.536a61.355 61.355 0 0 1 22.451 0 59.465 59.465 0 0 1 10.846 3.108 55 55 0 0 1 19.024 12.439 58.238 58.238 0 0 1 0 82.485l-29.143 29.2 24.515 24.445 4.631 4.751a57.534 57.534 0 0 1 17.115 41.173 58.292 58.292 0 0 1-4.4 22.407 58.2 58.2 0 0 1-53.811 35.956Zm-70.334-47.678 25.218 25.162 4.73 4.748a57.218 57.218 0 0 0 80.775-81.061l-.006-.006-4.632-4.752-25.216-25.144 29.852-29.909a57.238 57.238 0 0 0 0-81.069 54.007 54.007 0 0 0-18.681-12.217 58.461 58.461 0 0 0-10.663-3.055 60.354 60.354 0 0 0-22.084 0 58.461 58.461 0 0 0-10.663 3.055A54 54 0 0 0 157.9 16.788l-29.948 29.91-25.124-25.265-4.728-4.646A53.891 53.891 0 0 0 79.432 4.574a58.431 58.431 0 0 0-10.663-3.055 60.384 60.384 0 0 0-22.09 0 58.5 58.5 0 0 0-10.666 3.055 54 54 0 0 0-18.686 12.214 57.238 57.238 0 0 0 0 81.07l4.636 4.63 25.217 25.28-29.851 29.9A56.544 56.544 0 0 0 .5 198.137a57.3 57.3 0 0 0 4.327 22.024 56.362 56.362 0 0 0 12.5 18.568 57.019 57.019 0 0 0 80.776 0Zm70.381 21.377a33.611 33.611 0 0 1-12.273-2.293 31.079 31.079 0 0 1-10.641-6.876l-4.92-4.982 45.513-45.78 5.146 5.1a31.859 31.859 0 0 1 6.984 10.44 32.695 32.695 0 0 1-6.983 35.226 30.651 30.651 0 0 1-10.571 6.877 33.426 33.426 0 0 1-12.255 2.288Zm-22.209-9.874a30.085 30.085 0 0 0 10.3 6.653 32.98 32.98 0 0 0 23.8 0 29.659 29.659 0 0 0 10.229-6.654 31.294 31.294 0 0 0 0-44.25l-4.435-4.394-44.118 44.37Zm-118.4 9.874a33.463 33.463 0 0 1-12.264-2.293 30.418 30.418 0 0 1-10.554-6.879 32.165 32.165 0 0 1 0-45.664L64.7 145.332l45.707 45.8-29.82 29.9a30.63 30.63 0 0 1-10.593 6.874 33.555 33.555 0 0 1-12.273 2.293ZM64.7 146.75l-29.084 29.324a31.314 31.314 0 0 0 0 44.25 29.428 29.428 0 0 0 10.212 6.655 33.006 33.006 0 0 0 23.8 0 29.635 29.635 0 0 0 10.246-6.653l29.115-29.194Zm88.119 51.593-6.836-6.859-81.64-81.769-6.834-6.874 45.675-45.663 6.867 6.874 81.607 81.636 6.834 6.874Zm-93.9-95.5 6.132 6.163 81.637 81.769 6.129 6.149 44.262-44.367-6.131-6.167-81.605-81.632-6.16-6.166Zm94.65 63.863a13.334 13.334 0 0 1-13.245-13.391 13.231 13.231 0 1 1 13.245 13.391Zm0-25.664a12.316 12.316 0 0 0-12.245 12.273 12.23 12.23 0 1 0 20.867-8.667 12.1 12.1 0 0 0-8.622-3.607Zm-25.616 0a13 13 0 0 1-5.134-1.051 13.319 13.319 0 0 1-4.211-2.855 13.254 13.254 0 0 1 9.345-22.648 13.351 13.351 0 0 1 9.44 3.857 13.2 13.2 0 0 1 0 18.792 13.32 13.32 0 0 1-9.44 3.904Zm0-25.554a12.277 12.277 0 0 0 0 24.554 12.326 12.326 0 0 0 8.737-3.614 12.2 12.2 0 0 0 0-17.371 12.357 12.357 0 0 0-8.737-3.57Zm-25.522 0A13.347 13.347 0 0 1 93.1 92.729a13.255 13.255 0 0 1 22.607 9.36 13.353 13.353 0 0 1-13.276 13.398Zm0-25.664a12.3 12.3 0 0 0-12.214 12.265 12.246 12.246 0 1 0 24.49 0 12.331 12.331 0 0 0-12.277-12.265Zm88.869 20.245-45.672-45.663 29.788-29.909a30.775 30.775 0 0 1 10.606-6.947 33.717 33.717 0 0 1 24.527 0 30.776 30.776 0 0 1 10.607 6.947 31.725 31.725 0 0 1 6.981 10.426 32.714 32.714 0 0 1-6.983 35.237Zm-44.259-45.663 44.262 44.25 29.145-29.2a31.714 31.714 0 0 0 6.765-34.15 30.732 30.732 0 0 0-6.764-10.1 29.784 29.784 0 0 0-10.266-6.723 32.717 32.717 0 0 0-23.792 0 29.782 29.782 0 0 0-10.265 6.723ZM39.989 85.278l-5.083-5.119a32.15 32.15 0 0 1 0-45.661 32.11 32.11 0 0 1 45.679 0l4.952 4.98Zm17.725-59.32a30.554 30.554 0 0 0-22.095 9.24l-.006.006a31.314 31.314 0 0 0 0 44.247l4.376 4.408 44.138-44.381-4.256-4.28a30.629 30.629 0 0 0-22.157-9.24Z'
                      fill='rgba(0,0,0,0)'
                    ></path>
                    <path data-name='Rect\xE1ngulo 879' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='small-block-title'>距离上次健康检查</div>
                <div className='small-block-text'>{timeSinceLastHealActivity} days</div>
              </div>
            </Col>
            <Col span={7} offset={1}>
              <div className='small-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='small-block-icon-style'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <path
                      data-name='Uni\xF3n 17'
                      d='M.449 128.494A128.188 128.188 0 0 1 128.494.45h10.6v52.857a76.1 76.1 0 0 1 46.531 25.151 75.572 75.572 0 0 1 13.854 22.845 75.251 75.251 0 0 1 5.039 27.189 76.11 76.11 0 0 1-76.023 76.022 76.1 76.1 0 0 1-76.012-76.022 75.291 75.291 0 0 1 5.037-27.189 75.678 75.678 0 0 1 13.85-22.845 76.135 76.135 0 0 1 46.555-25.151v-31.18a106.369 106.369 0 0 0-19.6 3.814 106.378 106.378 0 0 0-18.193 7.25 107.579 107.579 0 0 0-16.385 10.312A108.253 108.253 0 0 0 49.54 56.524a108.229 108.229 0 0 0-11.676 15.37 107.348 107.348 0 0 0-8.787 17.356 106.17 106.17 0 0 0-7.459 39.244 107.008 107.008 0 0 0 106.877 106.892 107.017 107.017 0 0 0 106.9-106.892 10.5 10.5 0 0 1 3.1-7.479 10.49 10.49 0 0 1 7.475-3.1 10.593 10.593 0 0 1 10.584 10.58 128.2 128.2 0 0 1-128.057 128.057A128.2 128.2 0 0 1 .449 128.494Zm99.967-47.048a55.106 55.106 0 0 0-14.062 12.016 54.643 54.643 0 0 0-9.336 16.083 54.492 54.492 0 0 0-3.379 18.95 54.464 54.464 0 0 0 4.316 21.333 54.924 54.924 0 0 0 5.068 9.317 55.648 55.648 0 0 0 6.7 8.12 55.546 55.546 0 0 0 8.125 6.7 54.955 54.955 0 0 0 9.316 5.068 54.353 54.353 0 0 0 21.328 4.316 54.917 54.917 0 0 0 54.854-54.857 54.492 54.492 0 0 0-3.379-18.95 54.614 54.614 0 0 0-9.326-16.083 55.144 55.144 0 0 0-14.049-12.016 54.571 54.571 0 0 0-17.5-6.723v30.482a25.816 25.816 0 0 1 10.824 9.254 25.366 25.366 0 0 1 4.211 14.035 25.433 25.433 0 0 1-2.014 9.982 25.524 25.524 0 0 1-5.494 8.145 25.5 25.5 0 0 1-8.145 5.493 25.518 25.518 0 0 1-9.982 2.015 25.477 25.477 0 0 1-9.973-2.015 25.621 25.621 0 0 1-8.148-5.493 25.538 25.538 0 0 1-5.488-8.145 25.522 25.522 0 0 1-2.016-9.982 25.393 25.393 0 0 1 4.207-14.035 25.82 25.82 0 0 1 10.848-9.254V74.72a54.537 54.537 0 0 0-17.508 6.73Z'
                    ></path>
                    <path data-name='Rect\xE1ngulo 878' fill='none' d='M0 0h256v256H0z'></path>
                  </g>
                </svg>
                <div className='small-block-title'>距离上次扫描</div>
                <div className='small-block-text'>{timeSinceLastScanActivity} days</div>
              </div>
            </Col>
            <Col span={7} offset={1}>
              <div className='small-block'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='small-block-icon-style'
                  fill='currentcolor'
                  viewBox='0 0 256 256'
                >
                  <defs>
                    <clipPath id='prefix__a'>
                      <path d='M0 0h256v256H0z'></path>
                    </clipPath>
                  </defs>
                  <g clip-path='url(#prefix__a)'>
                    <path fill='none' d='M0 0h256v256H0z'></path>
                    <g data-name='UptimeIcon'>
                      <path data-name='Rect\xE1ngulo 851' fill='none' d='M0 0h256v256H0z'></path>
                      <g data-name='Grupo 1558'>
                        <path
                          data-name='Sustracci\xF3n 3'
                          d='M220.67 154.223h-10.627c.012-.6.016-1.149.016-1.669a82.374 82.374 0 0 0-1.073-13.283h-64.771v-78.9l25.611 11.287 45.143 34.182 4.232 33.5a53.041 53.041 0 0 1 5.371 4.445 22.28 22.28 0 0 1 3.4 3.962c.938 1.48 1.252 2.729.941 3.709-.577 1.836-3.35 2.767-8.243 2.767Z'
                          fill='#e3e3e3'
                        ></path>
                        <path
                          data-name='Uni\xF3n 9'
                          d='M24.003 152.341a102.96 102.96 0 0 1 24.863-67.172 104.134 104.134 0 0 1 61.651-35.019l.586-.1v22.866l-.4.084a81.178 81.178 0 0 0-64.137 79.337c0 44.762 36.557 81.18 81.492 81.18s81.492-36.418 81.492-81.18a80.636 80.636 0 0 0-18.828-51.854 81.865 81.865 0 0 0-20.838-17.8 80.846 80.846 0 0 0-26.053-10l-.408-.084V49.8l.582.089a103.267 103.267 0 0 1 34.789 11.962 104.595 104.595 0 0 1 27.953 22.727 103.042 103.042 0 0 1 25.363 67.76C232.114 209.5 185.437 256 128.062 256S24.003 209.5 24.003 152.341Zm104.625 9.91a10.07 10.07 0 0 1-1.023-.054c-4.723-.094-9.377-3.03-9.377-8.8V30.467l-10.9 10.113c-8.939 8.3-22.533-4.325-13.594-12.619l27.248-25.3a10.162 10.162 0 0 1 13.719 0l27.252 25.3c8.943 8.294-4.658 20.918-13.6 12.619L137.46 30.467v113.674h41.412a9.055 9.055 0 1 1 0 18.11Z'
                        ></path>
                      </g>
                    </g>
                  </g>
                </svg>
                <div className='small-block-title'>运行时间</div>
                <div className='small-block-text'>{uptime} days</div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>数据使用量</div>
                <ReactECharts
                  option={dataUsageOption}
                  autoResize={true}
                  className='echarts-style-1'
                />
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>数据分布</div>
                <ReactECharts
                  option={objectSizeOption}
                  autoResize={true}
                  className='echarts-style-1'
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>API数据接受率</div>
                <ReactECharts option={dataReceiveOption} className='echarts-style-1' />
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>API数据发送率</div>
                <ReactECharts option={dataSendOption} className='echarts-style-1' />
              </div>
            </Col>
          </Row>
        </Tabs.TabPane>
        <Tabs.TabPane tab='数据流量' key='2'>
          <Row>
            <Col style={{ marginTop: 5 }} span={1}>
              过滤：
            </Col>
            <Col span={19}>
              <RangePicker
                value={rangeValue}
                onChange={handleRangeChange}
                style={{ width: '100%' }}
                showTime
              />
            </Col>
            <Col span={2} offset={1}>
              <Button style={{ backgroundColor: 'rgb(84, 73, 43)', width: '100%' }} type='primary'>
                同步数据
              </Button>
            </Col>
          </Row>
          <Row>
            <Col span={23}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>API请求率</div>
                <ReactECharts option={apiRequestOption} className='echarts-style-2' />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>API请求错误率</div>
                <ReactECharts option={apiRequestErrorOption} className='echarts-style-1' />
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>节点数据传输</div>
                <ReactECharts option={internodeDataOption} className='echarts-style-1' />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={23}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>节点吞吐量</div>
                <ReactECharts option={nodeIOOption} className='echarts-style-2' />
              </div>
            </Col>
          </Row>
        </Tabs.TabPane>
        <Tabs.TabPane tab='资源使用' key='3'>
          <Row>
            <Col style={{ marginTop: 5 }} span={1}>
              过滤：
            </Col>
            <Col span={19}>
              <RangePicker
                value={rangeValue}
                onChange={handleRangeChange}
                style={{ width: '100%' }}
                showTime
              />
            </Col>
            <Col span={2} offset={1}>
              <Button style={{ backgroundColor: 'rgb(84, 73, 43)', width: '100%' }} type='primary'>
                同步数据
              </Button>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>节点内存使用</div>
                <ReactECharts option={nodeMemoryOption} className='echarts-style-1' />
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>节点CPU使用</div>
                <ReactECharts option={nodeCPUOption} className='echarts-style-1' />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>磁盘空闲节点</div>
                <ReactECharts option={drivesFreeOption} className='echarts-style-1' />
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>磁盘使用量</div>
                <ReactECharts option={drivesUsedOption} className='echarts-style-1' />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={11}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>节点系统调用</div>
                <ReactECharts option={nodeSyscallsOption} className='echarts-style-1' />
              </div>
            </Col>
            <Col span={11} offset={1}>
              <div className='dashboard-block'>
                <div className='dashboard-block-title'>节点文件描述符</div>
                <ReactECharts option={nodeFileOption} className='echarts-style-1' />
              </div>
            </Col>
          </Row>
        </Tabs.TabPane>
      </Tabs>
    </>
  )
}
